import { AsyncPipe, NgForOf, NgIf, NgOptimizedImage } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import {
  MatFormField,
  MatFormFieldModule,
  MatLabel,
} from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { assert } from '@sama/js-core';
import { map } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { BehaviorSubject, combineLatest, map as rxjsMap, merge } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { UuidService } from '../../../../cross-team-shared/misc-services/uuid.service';
import { ScrollEnablerDirective } from '../../../../cross-team-shared/scroll-enabler.directive';
import { ObjectWith } from '../../../../cross-team-shared/types/misc';
import { AbstractAnnotationStore } from '../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { AutocompleteChipsComponent } from '../../../../shared/misc-components/autocomplete-chips-input.component';
import { LoadImageService } from '../../../../shared/misc-services/load-image.service';
import {
  Attachment,
  ClientFeedbackType,
} from '../../../../team-apis/otterdam/feedback';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { Screenshot } from '../../resources/client-issue-resource.service';
import { ClientFeedbackDialogService } from '../client-feedback-dialog.service';
import { ClientFeedbackCrudService } from '../crud/client-feedback-crud.service';
import { FeedbackListService } from '../feedback-list.service';
import { FeedbackPaneComponent } from '../feedback-pane/feedback-pane.component';

const CLIENT_ISSUE_TYPES: ObjectWith<ClientFeedbackType> = {
  'Instruction gap': 'instruction-gap',
  Subjectivity: 'subjectivity',
  'Sensor issue': 'sensor-issue',
  'Edge case': 'edge-case',
  Other: 'other',
};

@Component({
  selector: 'h3-add-edit-client-issue-dialog',
  templateUrl: './add-edit-client-issue-dialog.component.html',
  styleUrls: [
    './add-edit-client-issue-dialog.component.scss',
    '../feedback.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FeedbackPaneComponent,
    AsyncPipe,
    ReactiveFormsModule,
    MatFormField,
    MatSelect,
    NgForOf,
    NgIf,
    MatIcon,
    MatCheckbox,
    MatTooltip,
    MatOption,
    NgOptimizedImage,
    AutocompleteChipsComponent,
    MatButton,
    MatInput,
    MatLabel,
    MatFormFieldModule,
    ScrollEnablerDirective,
  ],
})
export class AddEditClientFeedbackDialogComponent
  extends DirectiveSuperclass
  implements AfterViewInit
{
  clientFeedbackTypes = CLIENT_ISSUE_TYPES;
  clientFeedbackTypeLabels = Object.keys(CLIENT_ISSUE_TYPES);
  feedbackListService = inject(FeedbackListService);
  form = this.formBuilder.nonNullable.group({
    issueType: ['' as ClientFeedbackType, Validators.required],
    title: '',
    description: '',
    parkTask: false,
    screenshots: [[] as Screenshot[]],
    tags: [[] as string[]],
  });
  dialogTitle$ = new BehaviorSubject('Create an issue');
  disabledParkTaskTooltip?: string;
  clientFeedbackTags$ = combineLatest([
    this.form.controls.tags.valueChanges.pipe(
      startWith(this.form.controls.tags.value),
    ),
    this.taskWorkspaceStore('clientFeedbackTags').$,
  ]).pipe(
    rxjsMap(([tags, clientFeedbackTags]) =>
      tags.length
        ? clientFeedbackTags.filter((tag) => !tags.includes(tag))
        : clientFeedbackTags,
    ),
  );
  attachments$ = new BehaviorSubject([] as Attachment[]);
  selectedIssueId$ = this.feedbackListService.getSelectedId$();
  private uuidService = inject(UuidService);
  private target$ = this.taskWorkspaceStore('feedbackPopover')('target').$;

  constructor(
    private clientFeedbackDialogService: ClientFeedbackDialogService,
    private clientFeedbackCrudService: ClientFeedbackCrudService,
    private formBuilder: FormBuilder,
    private store: AbstractAnnotationStore,
    private taskWorkspaceStore: TaskWorkspaceStore,
    private loadImageService: LoadImageService,
    injector: Injector,
  ) {
    super(injector);
  }

  ngAfterViewInit() {
    this.subscribeTo(
      merge(
        this.clientFeedbackDialogService.getSelectedFeedback$(),
        this.target$,
      ),
      () => {
        this.initFormValue();
        this.initTitle();
      },
    );
  }

  saveIssue() {
    const formValues = this.form.getRawValue();
    const coordinates =
      this.taskWorkspaceStore.state().feedbackPopover.missingShapePoint;
    assert(coordinates, 'Point are always defined when opening the dialog');
    const issueToUpdate =
      this.clientFeedbackDialogService.getSelectedFeedback();
    this.clientFeedbackDialogService.closeDialog();
    this.clientFeedbackCrudService.save(
      {
        id: issueToUpdate?.id ?? this.uuidService.getUuidV4(),
        type: formValues.issueType,
        title: formValues.title,
        description: formValues.description,
        tags: formValues.tags,
        parkTasks: formValues.parkTask,
        location: {
          frame_number: this.store.state().frameNumber,
          coordinates,
          output_id:
            issueToUpdate?.location.output_id ??
            this.taskWorkspaceStore.getSelectedOutput().id,
        },
      },
      formValues.screenshots,
      !!issueToUpdate,
    );
    this.feedbackListService.openFeedbackList('reopen');
  }

  closeDialog() {
    this.clientFeedbackDialogService.closeDialog();
  }

  async addScreenshots(event: any) {
    const files = event.target.files;
    if (!files) {
      return;
    }
    const screenshots = await Promise.all(
      map(files, async (file: File) => {
        const arrayBuffer = await this.loadImageService.loadImageBlob(file);
        const blob = new Blob([arrayBuffer], { type: file.type });
        const url = URL.createObjectURL(blob);
        return { name: file.name, url, blob };
      }),
    );
    this.form.controls.screenshots.setValue([
      ...this.form.controls.screenshots.value,
      ...screenshots,
    ]);
  }

  private initFormValue() {
    const selectedIssueId =
      this.clientFeedbackDialogService.getSelectedFeedback()?.id;
    if (selectedIssueId) {
      const issue = this.clientFeedbackCrudService.getFeedback(selectedIssueId);
      assert(issue, 'Issue is always defined when editing it');
      this.form.patchValue({
        issueType: issue.type,
        title: issue.title,
        description: issue.description,
        parkTask: issue.parkTasks,
        tags: issue.tags,
      });
      // fix me if you change this code
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
      this.attachments$.next(issue.attachments ?? []);
      this.form.controls.parkTask.disable();
    } else {
      this.form.reset();
      this.form.controls.parkTask.enable();
    }
    const taskState = this.taskWorkspaceStore.state().task.state;
    const validTaskStates = ['new', 'in progress', 'rejected'];
    if (taskState && !validTaskStates.includes(taskState)) {
      this.form.controls.parkTask.disable();
      this.disabledParkTaskTooltip =
        'Cannot park tasks that are already annotated';
    }
  }

  private initTitle() {
    const selectedIssueId =
      this.clientFeedbackDialogService.getSelectedFeedback();
    this.dialogTitle$.next(selectedIssueId ? 'Edit issue' : 'Create an issue');
  }
}
